import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface Supplier {
  id: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  productsSupplied: number;
  totalOrders: number;
  pendingOrders: number;
  rating: number;
  status: "active" | "inactive" | "pending";
  paymentTerms: string;
  lastOrder: string;
}

const MOCK_SUPPLIERS: Supplier[] = [
  { id: "SUP-001", name: "TechSupply Co.", contactPerson: "Alex Johnson", email: "alex@techsupply.com", phone: "+1 555-0101", address: "100 Tech Park, Silicon Valley, CA", productsSupplied: 45, totalOrders: 128, pendingOrders: 2, rating: 4.8, status: "active", paymentTerms: "Net 30", lastOrder: "2024-01-14" },
  { id: "SUP-002", name: "SportWear Inc.", contactPerson: "Maria Garcia", email: "maria@sportwear.com", phone: "+1 555-0102", address: "200 Sports Ave, Denver, CO", productsSupplied: 32, totalOrders: 86, pendingOrders: 1, rating: 4.5, status: "active", paymentTerms: "Net 45", lastOrder: "2024-01-12" },
  { id: "SUP-003", name: "MobileGear Ltd.", contactPerson: "David Chen", email: "david@mobilegear.com", phone: "+1 555-0103", address: "300 Mobile St, New York, NY", productsSupplied: 28, totalOrders: 64, pendingOrders: 0, rating: 4.2, status: "active", paymentTerms: "Net 30", lastOrder: "2024-01-10" },
  { id: "SUP-004", name: "FashionWholesale", contactPerson: "Emma Brown", email: "emma@fashionwholesale.com", phone: "+1 555-0104", address: "400 Fashion Blvd, Los Angeles, CA", productsSupplied: 56, totalOrders: 142, pendingOrders: 3, rating: 4.6, status: "active", paymentTerms: "Net 60", lastOrder: "2024-01-15" },
  { id: "SUP-005", name: "HomeGoods Direct", contactPerson: "James Wilson", email: "james@homegoods.com", phone: "+1 555-0105", address: "500 Home Ave, Chicago, IL", productsSupplied: 0, totalOrders: 0, pendingOrders: 0, rating: 0, status: "pending", paymentTerms: "TBD", lastOrder: "-" },
];

export default function SuppliersSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const filteredSuppliers = MOCK_SUPPLIERS.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.contactPerson.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || item.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const activeSuppliers = MOCK_SUPPLIERS.filter((s) => s.status === "active").length;
  const totalProducts = MOCK_SUPPLIERS.reduce((acc, s) => acc + s.productsSupplied, 0);
  const pendingOrders = MOCK_SUPPLIERS.reduce((acc, s) => acc + s.pendingOrders, 0);
  const avgRating = (MOCK_SUPPLIERS.filter((s) => s.rating > 0).reduce((acc, s) => acc + s.rating, 0) / MOCK_SUPPLIERS.filter((s) => s.rating > 0).length).toFixed(1);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return { color: "text-emerald-500 bg-emerald-500/20", label: t("posInventory.active") };
      case "inactive":
        return { color: "text-red-500 bg-red-500/20", label: t("posInventory.inactive") };
      case "pending":
        return { color: "text-amber-500 bg-amber-500/20", label: t("posInventory.pendingApproval") };
      default:
        return { color: "text-gray-500 bg-gray-500/20", label: status };
    }
  };

  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <i
          key={i}
          className={`ri-star-${i <= rating ? "fill" : "line"} ${i <= rating ? "text-amber-400" : themeClasses.textSecondary}`}
        ></i>
      );
    }
    return stars;
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posInventory.suppliersTitle")}</h1>
          <p className={themeClasses.textSecondary}>{t("posInventory.suppliersSubtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center gap-2`}>
            <i className="ri-download-2-line"></i>
            {t("posInventory.export")}
          </button>
          <button className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2">
            <i className="ri-add-line"></i>
            {t("posInventory.addSupplier")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.activeSuppliers")}</span>
            <i className="ri-truck-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{activeSuppliers}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.productsSupplied")}</span>
            <i className="ri-box-3-line text-blue-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{totalProducts}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.pendingOrders")}</span>
            <i className="ri-time-line text-amber-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-amber-500`}>{pendingOrders}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.avgRating")}</span>
            <i className="ri-star-fill text-amber-400 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{avgRating}</p>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row gap-4`}>
          <div className="flex-1 relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              type="text"
              placeholder={t("posInventory.searchSuppliers")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            >
              <option value="all">{t("posInventory.allStatuses")}</option>
              <option value="active">{t("posInventory.active")}</option>
              <option value="inactive">{t("posInventory.inactive")}</option>
              <option value="pending">{t("posInventory.pending")}</option>
            </select>
            <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} flex items-center gap-2`}>
              <i className="ri-filter-line"></i>
              {t("posInventory.filters")}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.supplier")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.contact")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.products")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.orders")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.rating")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.terms")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.status")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredSuppliers.map((supplier) => {
                const statusBadge = getStatusBadge(supplier.status);
                return (
                  <tr key={supplier.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-violet-500/20`}>
                          <i className="ri-building-line text-violet-500 text-lg"></i>
                        </div>
                        <div>
                          <p className={`font-medium ${themeClasses.text}`}>{supplier.name}</p>
                          <p className={`text-sm ${themeClasses.textSecondary}`}>{supplier.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className={`font-medium ${themeClasses.text}`}>{supplier.contactPerson}</p>
                        <p className={`text-sm ${themeClasses.textSecondary}`}>{supplier.email}</p>
                      </div>
                    </td>
                    <td className={`px-4 py-3 text-right font-medium ${themeClasses.text}`}>{supplier.productsSupplied}</td>
                    <td className="px-4 py-3 text-right">
                      <div>
                        <p className={`font-medium ${themeClasses.text}`}>{supplier.totalOrders}</p>
                        {supplier.pendingOrders > 0 && (
                          <p className="text-sm text-amber-500">{supplier.pendingOrders} {t("posInventory.pending")}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {supplier.rating > 0 ? (
                        <div className="flex items-center justify-center gap-0.5">
                          {renderStars(Math.round(supplier.rating))}
                          <span className={`ml-1 text-sm ${themeClasses.textSecondary}`}>({supplier.rating})</span>
                        </div>
                      ) : (
                        <span className={themeClasses.textSecondary}>-</span>
                      )}
                    </td>
                    <td className={`px-4 py-3 ${themeClasses.textSecondary}`}>{supplier.paymentTerms}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge.color}`}>
                        {statusBadge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`} title={t("posInventory.viewDetails")}>
                          <i className="ri-eye-line"></i>
                        </button>
                        <button className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`} title={t("posInventory.createOrder")}>
                          <i className="ri-shopping-cart-line"></i>
                        </button>
                        <button className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`} title={t("posInventory.edit")}>
                          <i className="ri-edit-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
